// Script capability manifest (SCR-16, ARCH §27.11, E-47). PURE (ENG-1) — the
// verb→capability table (ARCH §27.5 / SCRIPTING §5.3) plus the set computation the
// compiler folds over an already-parsed body.
//
// §27.11's whole point is that a manifest is VERIFIED, not trusted. The verb table is
// a whitelist and the user call graph is acyclic (`assertAcyclic`, parse.ts), so the
// compiler can COMPUTE every capability a program can reach and then enforce
// `declared ⊇ used`; an undeclared verb is a compile error naming the verb AND the
// capability it needs (E-47). The editor and the install prompt display the COMPUTED
// set, never the declared one — the only reason showing a manifest to a server owner
// is worth anything, since a manifest then cannot understate a script's powers.
//
// Three properties this module holds to:
//
//   • COVERAGE — every name in `VERB_SIGNATURES` has an entry here. "Needs no
//     capability" (the builtin row of §27.5, written `—` there) is the EMPTY-STRING
//     capability, spelled `NONE` below — never a missing key, because a missing key
//     would read exactly like a verb somebody forgot. `tests/script_caps.test.ts`
//     asserts the two tables have identical key sets, so a verb added without a
//     capability fails loudly instead of silently needing none.
//
//   • DETERMINISM — a computed set iterates in SORTED (ASCII) order, never traversal
//     order, so the same source yields the same set AND the same order on every
//     machine: what the editor lists, what a diff compares, what an install prompt
//     shows, what §27.12's hash covers.
//
//   • TOTALITY — nothing here throws. A call the parser will reject (unknown name,
//     non-callable callee) is skipped rather than diagnosed twice, and each fn body
//     folds in at most once. That once-only fold is a FOLD, not a cycle guard:
//     parse.ts has already proven the graph acyclic, and re-walking a diamond
//     (a→b, a→c, b→d, c→d) would only cost time.
//
// The table is checked against `VERB_SIGNATURES` by the test, not by an import here:
// this module stays a leaf over the AST types, so nothing in the verb-dispatch path
// can come to depend on it (and vice versa).
import type { Expr, FnDecl, Program, Stmt } from './lang/parse';

/** The capability a verb requires, e.g. 'world.read' / 'world.write'. */
export type Capability = string;

/** The capability of a verb that needs none — §27.5's builtin row. A real capability
 *  name is never empty, so `if (cap)` is a total test for "this verb is host-free",
 *  and `''` is never added to a computed set nor required of a declaration. */
const NONE: Capability = '';

/** verb name (as it appears in VERB_SIGNATURES) → the capability it requires.
 *
 *  Transcribed from ARCH §27.5's table (= SCRIPTING §5.3, "The verb table, sliced by
 *  host"), which is BINDING: this table IS the sandbox, and its capability column is
 *  what §27.11 computes and §27.12 gates on. Every §27.5 row that names a verb this
 *  build has appears below; see the note after the table for the rows whose verbs
 *  arrive with later milestones. */
export const VERB_CAPABILITIES: Readonly<Record<string, Capability>> = {
  // §27.5: "getblock, detect, nearestEntity, nearestPlayer, playerWithin,
  // entityWithin → world.read" — "reads are cheap and harmless" (SCRIPTING §5.3).
  getblock: 'world.read',
  detect: 'world.read',
  nearestEntity: 'world.read',
  nearestPlayer: 'world.read',
  playerWithin: 'world.read',
  entityWithin: 'world.read',
  // §27.5: "setblock → world.write" and "fill (≤ SCR.FILL_MAX) → world.write".
  setblock: 'world.write',
  fill: 'world.write',
  // §27.5: "summon → entity.spawn".
  summon: 'entity.spawn',
  // §27.5: "give → player.give" — "item duplication from a mob loop" is the risk.
  give: 'player.give',
  // §27.5: "say → chat" (rate-limited for mobs, §27.8).
  say: 'chat',
  // §27.5: "emit | ✅ | ✅ | signal" — entities coordinate by bounded signals rather
  // than by calling each other (§27.10). M46.3.
  emit: 'signal',
  // §27.5: "aircraft.setControl → aircraft" (AVI-3, a block/player affordance).
  'aircraft.setControl': 'aircraft',
  // §27.5: "self.moveTo/moveAway/face/faceAt/jump/setTarget/clearTarget/attack/hurt/
  // heal/despawn → self" — the mob slice's steering and self-effects (M45.4). One
  // capability covers the whole row: "there is no 'self' to steer on a block"
  // (SCRIPTING §5.3), so a script that reaches ANY of them is a script that drives an
  // entity, and splitting the row would let a manifest look tamer than it is.
  'self.moveTo': 'self',
  'self.moveAway': 'self',
  'self.face': 'self',
  'self.faceAt': 'self',
  'self.jump': 'self',
  'self.setTarget': 'self',
  'self.clearTarget': 'self',
  'self.attack': 'self',
  'self.hurt': 'self',
  'self.heal': 'self',
  'self.despawn': 'self',
  // §27.5: "mem.get/set → —" and "handled() → —". Per-entity memory (§27.7) is the
  // entity's OWN bag, never `Entity.data` and never replicated, and `handled()` only
  // suppresses the archetype for one dispatch (§27.6) — neither reaches the world, a
  // player or another entity, so neither grants reach a script did not already have.
  'mem.get': NONE,
  'mem.set': NONE,
  handled: NONE,
  // §27.5 builtin row: "vec3,abs,floor,ceil,round,sqrt,sign,min,max,clamp,list,at,
  // len,push,distance,random,chance → —" ("deterministic, no host"). The list verbs
  // (SCR-5) are pure value operations — immutable, host-free, bounded by
  // SCR.LIST_MAX — so they need no capability, like the numeric builtins.
  // §27.8's seeded randomness is host-free in the capability sense: the draw comes
  // from the per-dispatch `scriptRng(entitySeed, tick)` the caller supplies, not from
  // the world or the player, so it grants a script no reach it did not already have.
  random: NONE,
  chance: NONE,
  list: NONE,
  at: NONE,
  len: NONE,
  push: NONE,
  vec3: NONE,
  distance: NONE,
  abs: NONE,
  floor: NONE,
  ceil: NONE,
  round: NONE,
  sqrt: NONE,
  sign: NONE,
  min: NONE,
  max: NONE,
  clamp: NONE,
};

// Every §27.5 row now has its verb: M46.3 added `emit` → 'signal' above, which was the
// last one this list was waiting on.
// M45.4 added the rest of §27.5's mob rows above (`self.*` → 'self'; `mem.get/set`,
// `handled`, `distance` → NONE). The three read queries stayed out on purpose: they
// are ✅/✅ rows, so they belong on `ScriptHost` — which the client, the server and
// every test implement — and adding a required method there is an entity-host change,
// not a verb-table one (M45.3/M45.8's wiring).
// Every verb §27.5 names carries a capability column entry — `—` for the host-free
// row — so there is no verb in the table this module has to guess at.

/** What one walk carries: the fn table calls resolve against, the fn bodies already
 *  folded in (at most once each), and the sink. `visit` is called ONLY for a verb
 *  that requires a capability, and returns false to stop the walk — which is how
 *  `checkDeclared` reports the FIRST offending verb in source order and no others. */
interface Walk {
  readonly fns: ReadonlyMap<string, FnDecl>;
  readonly folded: Set<string>;
  readonly visit: (verb: string, capability: Capability) => boolean;
}

/** Every capability reachable from a body, following user `fn` calls through the
 *  (acyclic) call graph. Deterministic: same source ⇒ same set, same iteration order.
 *
 *  This is the set §27.11 calls COMPUTED — the one the editor's capability panel, the
 *  install prompt (§27.12) and the "offer to write it in" back-compat path display. */
export function computeCapabilities(
  body: readonly Stmt[],
  fns: ReadonlyMap<string, FnDecl>,
): ReadonlySet<Capability> {
  const used = new Set<Capability>();
  walkStmts(body, { fns, folded: new Set(), visit: (_verb, cap) => { used.add(cap); return true; } });
  // Sorted, not insertion-ordered: the iteration order must not depend on where in
  // the source a verb happened to appear, or on whether a fn was inlined first.
  return new Set([...used].sort());
}

/** The COMPUTED capability set of a whole compiled program — `computeCapabilities` over
 *  its top-level body with its `fn` table indexed. This is the set §27.12's tier gate
 *  checks and an install refusal names (E-48): what the program CAN reach, never what its
 *  `capabilities { … }` block claims. */
export function programCapabilities(program: Program): ReadonlySet<Capability> {
  return computeCapabilities(program.body, new Map(program.fns.map((fn) => [fn.name, fn] as const)));
}

/** `declared ⊇ used`. Returns the offending verb + capability, or null if satisfied.
 *
 *  `declared === null` means the source has NO `capabilities` block: §27.11's
 *  back-compat rule makes its computed set its declaration, so such a source is
 *  always satisfied — call `computeCapabilities` for the set to report (or to write
 *  the block in). An offence is reported as a pair so the caller can name BOTH halves
 *  in one E-47 message, e.g.
 *    `verb 'setblock' requires capability 'world.write', which this script's
 *     capabilities block does not declare`.
 *  Verbs requiring no capability can never offend — they are never even visited. */
export function checkDeclared(
  declared: ReadonlySet<Capability> | null,
  body: readonly Stmt[],
  fns: ReadonlyMap<string, FnDecl>,
): { verb: string; capability: Capability } | null {
  if (declared === null) return null;
  const offences: { verb: string; capability: Capability }[] = [];
  walkStmts(body, {
    fns,
    folded: new Set(),
    visit: (verb, capability) => {
      if (declared.has(capability)) return true;
      offences.push({ verb, capability });
      return false; // stop at the first offence — source order, so the message is stable
    },
  });
  return offences[0] ?? null;
}

// ── The walk: statements → expressions → calls → fn bodies ─────────────────────
// Each walker returns false once `visit` has asked to stop. The switches below are
// exhaustive over parse.ts's unions with no `default`, so a statement or expression
// kind added later fails to typecheck HERE rather than quietly hiding a verb from the
// computed set — the same loud-failure discipline as the key-set parity test.

function walkStmts(stmts: readonly Stmt[], w: Walk): boolean {
  for (const s of stmts) if (!walkStmt(s, w)) return false;
  return true;
}

function walkStmt(s: Stmt, w: Walk): boolean {
  switch (s.kind) {
    case 'let':
    case 'assign':
    case 'return':
    case 'expr':
      return walkExpr(s.expr, w);
    case 'if':
      // Both branches count: the computed set is what a program CAN reach, not what
      // one run does (cf. §27.3's cost, which takes the larger branch instead).
      if (!walkExpr(s.cond, w) || !walkStmts(s.then, w)) return false;
      return s.otherwise ? walkStmts(s.otherwise, w) : true;
    case 'repeat':
      return walkExpr(s.count, w) && walkStmts(s.body, w);
    case 'on':
      return walkStmts(s.body, w);
  }
}

function walkExpr(e: Expr, w: Walk): boolean {
  switch (e.kind) {
    case 'num':
    case 'str':
    case 'bool':
    case 'var':
      return true;
    case 'unary':
      return walkExpr(e.operand, w);
    case 'binary':
      return walkExpr(e.left, w) && walkExpr(e.right, w);
    case 'member':
      return walkExpr(e.obj, w);
    case 'call':
      return walkCall(e, w);
  }
}

function walkCall(e: Expr & { kind: 'call' }, w: Walk): boolean {
  const name = calleeName(e.callee);
  if (name === null) {
    // Not callable — parse.ts rejects it; walk the sub-expressions anyway so this
    // module's answer never depends on the order the two passes run in.
    if (!walkExpr(e.callee, w)) return false;
  } else {
    const cap = VERB_CAPABILITIES[name];
    if (cap !== undefined) {
      if (cap !== NONE && !w.visit(name, cap)) return false;
    } else if (!foldFn(name, w)) {
      return false;
    }
  }
  for (const a of e.args) if (!walkExpr(a, w)) return false;
  return true;
}

/** Fold a user `fn`'s body into the walk — this is what makes a verb reachable ONLY
 *  through a fn appear in the computed set (§27.11: "possible because … the call
 *  graph is acyclic"). An unknown name is left to parse.ts's "unknown function". */
function foldFn(name: string, w: Walk): boolean {
  const fn = w.fns.get(name);
  if (!fn || w.folded.has(name)) return true;
  w.folded.add(name);
  return walkStmts(fn.body, w);
}

/** The callable name of a callee expression, shaped exactly like parse.ts's own
 *  resolution (`say` / `aircraft.setControl`), or null if it is not a name at all. */
function calleeName(callee: Expr): string | null {
  if (callee.kind === 'var') return callee.name;
  if (callee.kind === 'member' && callee.obj.kind === 'var') return `${callee.obj.name}.${callee.prop}`;
  return null;
}
